/**
 * Pagination utilities for API functions
 * AdminMunicipal 6.0 - SCALE-CRIT-001 Fix
 */

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationInfo;
}

/**
 * Calculate pagination range for Supabase queries
 * @param params Pagination parameters
 * @returns Object with from, to indices and normalized page/pageSize
 */
export function calculatePaginationRange(params?: PaginationParams): {
  from: number;
  to: number;
  page: number;
  pageSize: number;
} {
  const page = Math.max(1, params?.page ?? 1);
  const pageSize = Math.min(Math.max(1, params?.pageSize ?? DEFAULT_PAGE_SIZE), MAX_PAGE_SIZE);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  return { from, to, page, pageSize };
}

/**
 * Build pagination info from count and current position
 * @param totalCount Total number of items
 * @param page Current page number
 * @param pageSize Items per page
 * @returns PaginationInfo object
 */
export function buildPaginationInfo(
  totalCount: number,
  page: number,
  pageSize: number
): PaginationInfo {
  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    page,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}
